class FooterAccordion extends HTMLElement {
  constructor() {
    super();
    this.isMobile = window.innerWidth < 750;
    this.hasOpenClass = this.classList.contains('footer-menu-accordion--open');
    
    // On desktop, always open. On mobile, only open if has footer-menu-accordion--open class
    this.isOpen = this.isMobile ? this.hasOpenClass : true;
    
    // Bind methods
    this.handleClick = this.handleClick.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    // Get elements
    this.summary = this.querySelector('.footer-menu-accordion__summary');
    this.content = this.querySelector('.footer-block__details-content');
    this.icon = this.querySelector('.footer-menu-accordion__icon');
    
    if (!this.summary || !this.content) return;
    
    // Set initial state
    this.updateState();
    
    // Add click handler only on mobile
    if (this.isMobile) {
      this.summary.addEventListener('click', this.handleClick);
    }
    
    // Listen for resize events
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(this.handleResize, 250);
    });
  }

  handleClick(e) {
    // Only toggle on mobile
    if (this.isMobile) {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    }
  }

  toggle() {
    if (!this.isMobile) return;
    this.isOpen = !this.isOpen;
    this.updateState();
  }

  calculateMaxHeight() {
    // Calculate max-height based on:
    // - Each item: 16px height
    // - Gap between items: 8px (N-1 gaps for N items)
    // - Top padding: 0.8rem
    // - Bottom padding: 24px
    const listItems = this.content.querySelectorAll('li');
    const itemCount = listItems.length;
    
    if (itemCount === 0) return 0;
    
    // Get computed padding values
    const computedStyle = window.getComputedStyle(this.content);
    // Get the actual padding values when open (0.8rem top, 24px bottom)
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const topPadding = 0.8 * rootFontSize; // 0.8rem converted to px
    const bottomPadding = 24; // User specified 24px
    
    // Formula: topPadding + (itemCount * 16px) + ((itemCount - 1) * 8px) + bottomPadding
    // Items height: each item is 16px
    const itemsHeight = itemCount * 16;
    // Gaps: 8px between each item (N-1 gaps for N items)
    const gapsHeight = itemCount > 1 ? (itemCount - 1) * 8 : 0;
    // Total height
    const maxHeight = topPadding + itemsHeight + gapsHeight + bottomPadding;
    
    return Math.ceil(maxHeight); // Round up to avoid cutting off content
  }

  updateState() {
    // Calculate content height for smooth animation
    if (this.isOpen) {
      this.setAttribute('open', '');
      // Calculate max-height based on item count, gaps, and padding
      const maxHeight = this.calculateMaxHeight();
      this.content.style.maxHeight = maxHeight + 'px';
      this.content.style.opacity = '1';
      if (this.icon) {
        this.icon.style.transform = 'rotate(180deg)';
      }
    } else {
      this.removeAttribute('open');
      this.content.style.maxHeight = '0';
      this.content.style.opacity = '0';
      if (this.icon) {
        this.icon.style.transform = 'rotate(0deg)';
      }
    }
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 750;
    
    // If switching between mobile and desktop
    if (wasMobile !== this.isMobile) {
      if (this.isMobile) {
        // Switched to mobile - add click handler
        if (this.summary) {
          this.summary.addEventListener('click', this.handleClick);
        }
        // Close all except first on mobile
        this.isOpen = this.hasOpenClass;
      } else {
        // Switched to desktop - remove click handler, open all
        if (this.summary) {
          this.summary.removeEventListener('click', this.handleClick);
        }
        this.isOpen = true;
      }
      this.updateState();
    }
  }

  disconnectedCallback() {
    if (this.summary) {
      this.summary.removeEventListener('click', this.handleClick);
    }
  }
}

// Register the custom element
customElements.define('footer-menu-accordion', FooterAccordion);
